"use client";

import clsx from "clsx";
import { Monogram } from "@/components/persona/Monogram";
import { TradeDossier } from "@/components/sign/TradeDossier";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Screen 3.6 — T1 Reveal. Both dashboards pull back. The living-reputation
 * moment: Al Madar's Counterparty Profile is born (`0 → 1 trades`); Aurora's
 * profile compounds (`24 → 25 trades`). Time-elapsed counter: 11 days
 * simulated vs 42 days pre-Orbitra Global.
 */
export function T1Reveal({
  alMadarBefore,
  alMadarAfter,
  auroraBefore,
  auroraAfter,
  className,
}: {
  alMadarBefore: Record<string, unknown> | null;
  alMadarAfter:  Record<string, unknown> | null;
  auroraBefore:  Record<string, unknown> | null;
  auroraAfter:   Record<string, unknown> | null;
  className?: string;
}) {
  const t = useTranslations("t1Scenario");
  return (
    <div className={clsx("flex h-full flex-col gap-4", className)}>
      <div className="glass p-5">
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">{t("revealKicker")}</p>
        <h2 className="mt-1 font-display text-2xl font-semibold text-orbitra-paper">{t("revealTitle")}</h2>
        <p className="mt-1 text-[12px] text-orbitra-teal-soft">
          {t("revealSummary")}
        </p>
      </div>

      <div className="grid flex-1 grid-cols-2 gap-4">
        <ReputationPane
          name={t("revealPaneAlMadarName")}
          monogramName="Al Madar Foods"
          flag="AE"
          accent="var(--state-verified)"
          headline={t("revealPaneAlMadarHeadline")}
          before={alMadarBefore ?? { verifiedTrades: 0 }}
          after={alMadarAfter ?? { verifiedTrades: 1, lifetimeVolumeUsd: 850000, onTimePct: 100, bankFinancedPct: 0 }}
          arcLabel={t("revealPaneArcAlMadar")}
        />
        <ReputationPane
          name={t("revealPaneAuroraName")}
          monogramName="Aurora Aves Brasil"
          flag="BR"
          accent="var(--state-verified)"
          headline={t("revealPaneAuroraHeadline")}
          before={auroraBefore ?? { verifiedTrades: 24, lifetimeVolumeUsd: 14_800_000 }}
          after={auroraAfter ?? { verifiedTrades: 25, lifetimeVolumeUsd: 15_650_000 }}
          arcLabel={t("revealPaneArcAurora")}
        />
      </div>

      <TradeDossier scenarioId="t1-onboarding" />

      <div className="glass flex items-center justify-between px-4 py-3 text-[11px]">
        <span className="text-orbitra-paper">
          {t("revealFooterTimeLabel")} <span className="font-mono text-state-traced">{t("revealFooterTimeValue")}</span>
        </span>
        <span className="text-orbitra-muted">
          {t("revealFooterPreOrbitraLabel")} <span className="font-mono">{t("revealFooterPreOrbitraValue")}</span>
        </span>
        <span className="text-orbitra-muted">
          {t("revealFooterAuditReady")}
        </span>
      </div>
    </div>
  );
}

function ReputationPane({
  name,
  monogramName,
  flag,
  accent,
  headline,
  before,
  after,
  arcLabel,
}: {
  name: string;
  monogramName: string;
  flag: string;
  accent: string;
  headline: string;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  arcLabel: string;
}) {
  const t = useTranslations("t1Scenario");
  return (
    <div className="glass flex flex-col p-5">
      <div className="flex items-center gap-3">
        <Monogram name={monogramName} flag={flag} size={40} accent={accent} />
        <div>
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{headline}</p>
          <p className="mt-0.5 font-display text-base font-semibold text-orbitra-paper">{name}</p>
        </div>
      </div>

      <StatGrid before={before} after={after} />

      <div className="mt-auto rounded-md border border-state-financed/30 bg-state-financed/8 p-3 text-[11px] text-state-financed">
        {t("reputationArcPrefix")}<span className="font-mono">{arcLabel}</span>
      </div>
    </div>
  );
}

function StatGrid({ before, after }: { before: Record<string, unknown>; after: Record<string, unknown> }) {
  const t = useTranslations("t1Reveal");
  return (
    <div className="mt-4 grid grid-cols-2 gap-3 text-[11px]">
      <Stat label={t("statVerifiedTrades")}    beforeV={fmt(before.verifiedTrades)}     afterV={fmt(after.verifiedTrades)}     unit="" />
      <Stat label={t("statLifetimeVolume")}    beforeV={fmtUsd(before.lifetimeVolumeUsd)} afterV={fmtUsd(after.lifetimeVolumeUsd)} unit="" />
      <Stat label={t("statOnTimeSettlement")}  beforeV={fmtPct(before.onTimePct)}        afterV={fmtPct(after.onTimePct)}       unit="" />
      <Stat label={t("statBankFinancedShare")} beforeV={fmtPct(before.bankFinancedPct)}  afterV={fmtPct(after.bankFinancedPct)} unit="" />
    </div>
  );
}

function Stat({ label, beforeV, afterV }: { label: string; beforeV: string; afterV: string; unit: string }) {
  const changed = beforeV !== afterV;
  return (
    <div className="rounded-md border border-state-verified/20 bg-orbitra-ink/40 px-3 py-2">
      <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</p>
      <div className="mt-1 flex items-baseline gap-2">
        <span className={clsx("font-mono text-[11px]", changed ? "text-orbitra-muted line-through" : "text-orbitra-paper")}>{beforeV}</span>
        {changed && <span className="font-mono text-[10px] text-state-financed">→</span>}
        {changed && <span className="font-display text-base font-semibold text-state-financed">{afterV}</span>}
      </div>
    </div>
  );
}

function fmt(v: unknown): string {
  if (v === undefined || v === null) return "—";
  return String(v);
}
function fmtUsd(v: unknown): string {
  if (v === undefined || v === null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  if (n >= 1_000_000) return `USD ${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `USD ${(n / 1_000).toFixed(0)}K`;
  return `USD ${n}`;
}
function fmtPct(v: unknown): string {
  if (v === undefined || v === null) return "—";
  return `${v}%`;
}
